// 10国・各国2地方。PROVINCES / p01〜p20 は既存APIとの互換のため地方IDとして維持する。
// 各地方は合戦4つ＋拠点。前線の砦→本城地方→国の制圧。100戦のID・クリア記録は維持。
// 国同士は隣接から分岐。国の制圧には2地方の拠点攻略が必要。
// 旧データですでに進出した地方は足場を保持し、クリア済みステージも常に再戦できる。
import type { Profile } from './profile';
import { stageKey } from './profile';
import { CONFIG } from './config';
import { CHAPTER_LEADERS } from '../data/leaders';

export type CountryId = `c${number}`;

export type ProvinceId = `p${number}`;

export interface ProvinceDef {
  id: ProvinceId;
  countryId: CountryId;
  /** 地方名（例：潮見地方） */
  name: string;
  /** 札に出す短い名（例：潮見） */
  short: string;
  /** 難易度 1..10（＝battle の章） */
  difficulty: number;
  /** battle の章の地形キー（CHAPTERS[].terrain と同じ） */
  terrain: string;
  /** 合戦4つのステージID（並びは国の詳細の一覧・地図の地点の順） */
  battles: readonly [number, number, number, number];
  /** 本城（城攻め）のステージID */
  castle: number;
  /** 戦場の名前（合戦4つ＋本城の順） */
  fields: readonly [string, string, string, string, string];
  /** 隣の地方 */
  adj: readonly ProvinceId[];
  /** 関所：制圧した国の数がこれ以上で通れる（0＝関所なし） */
  gate: number;
  /** 地図の上の代表点（島の絵に対する %、左上が 0,0） */
  pos: readonly [number, number];
  /** 本城を置く地方 */
  boss: boolean;
}

const KANJI_DIFF = ['壱', '弐', '参', '肆', '伍', '陸', '漆', '捌', '玖', '拾'];
/** 難易度の表記（壱〜拾。妖力の段階と紛れないよう Lv は使わない） */
export const difficultyLabel = (d: number) => KANJI_DIFF[Math.max(1, Math.min(10, d)) - 1];

const TERRAIN = ['meadow', 'river', 'bamboo', 'temple', 'snow', 'volcano', 'grave', 'mountain', 'oni', 'capital'];

/** 関所の条件（難易度 → 必要な制圧数） */
export const GATE_BY_DIFFICULTY: Record<number, number> = { 9: 6, 10: 7 };

// [id, 国名, 短い名, 難易度, 大将の国か, 合戦の名前4つ, 本城の名前, 代表点, 隣]
type Row = [ProvinceId, string, string, number, boolean, [string, string, string, string], string, [number, number], ProvinceId[]];
// 代表点・隣接は島の絵（art-src/worldmap/island.png）に合わせて決めた（国の領域は apps/web/src/data/worldmap-regions.ts）
const ROWS: Row[] = [
  ['p01', '潮見地方', '潮見', 1, false, ['浜の道', '漁村', '松原', '岬の砦'], '潮見砦', [26, 88], ['p02', 'p07']],
  ['p02', '青葉地方', '青葉', 1, true, ['田の畦', '紅葉坂', '里の社', '城下口'], '青葉城', [12, 72], ['p01', 'p03', 'p06']],
  ['p03', '水穂地方', '水穂', 2, false, ['河原道', '渡し場', '水車の里', '堤の陣'], '水穂砦', [30, 65], ['p02', 'p04', 'p05', 'p06', 'p07', 'p08']],
  ['p04', '川霧地方', '川霧', 2, true, ['霧の浅瀬', '舟着き場', '中洲', '水の関'], '川霧城', [50, 61], ['p03', 'p05', 'p08', 'p13']],
  ['p05', '若竹地方', '若竹', 3, false, ['竹の小径', '笹の原', '竹垣の砦', '炭焼き小屋'], '若竹砦', [38, 50], ['p03', 'p04', 'p06', 'p10', 'p09', 'p13']],
  ['p06', '風越地方', '風越', 3, true, ['風の峠道', '竹林の奥', '物見櫓', '竹の大門'], '風越城', [20, 57], ['p02', 'p03', 'p05']],
  ['p07', '古鐘地方', '古鐘', 4, false, ['参道', '苔の庭', '鐘楼', '朽ちた山門'], '古鐘砦', [42, 83], ['p01', 'p03', 'p08']],
  ['p08', '白鷺地方', '白鷺', 4, true, ['石段', '五重塔', '宿坊', '奥の院'], '白鷺城', [45, 70], ['p03', 'p04', 'p07']],
  ['p09', '霜月地方', '霜月', 5, false, ['雪の峠道', '氷の滝', '山小屋', '吹雪の尾根'], '霜月砦', [31, 41], ['p05', 'p10']],
  ['p10', '白嶺地方', '白嶺', 5, true, ['雪原', '凍て川', '雪の関', '白い谷'], '白嶺城', [46, 40], ['p05', 'p09', 'p14', 'p13', 'p16']],
  ['p11', '紅峰地方', '紅峰', 6, false, ['溶岩の原', '湯けむりの谷', '焼け野', '火口の縁'], '紅峰砦', [84, 44], ['p14', 'p17', 'p12']],
  ['p12', '焔谷地方', '焔谷', 6, true, ['灰の道', '硫黄の沢', '火の祠', '赤い岩場'], '焔谷城', [77, 57], ['p11', 'p13']],
  ['p13', '霧原地方', '霧原', 7, false, ['霧の野', '古い墓所', '卒塔婆の丘', '鬼火の沼'], '霧原砦', [60, 52], ['p04', 'p05', 'p10', 'p14', 'p12']],
  ['p14', '月影地方', '月影', 7, true, ['月夜の原', '無縁塚', '枯れ柳', '幽かな社'], '月影城', [66, 40], ['p10', 'p11', 'p18', 'p13', 'p16', 'p17']],
  ['p15', '雲居地方', '雲居', 8, false, ['雲の桟道', '岩屋', '修験の道', '天狗の松'], '雲居砦', [45, 21], ['p18', 'p16', 'p20']],
  ['p16', '鳴神地方', '鳴神', 8, true, ['雷の尾根', '大杉', '鎖場', '天狗の庵'], '鳴神城', [52, 30], ['p10', 'p14', 'p18', 'p15']],
  ['p17', '鬼岳地方', '鬼岳', 9, false, ['岩礁', '鬼の砦', '黒い浜', '洞窟の口'], '鬼岳砦', [84, 22], ['p14', 'p11', 'p18', 'p19']],
  ['p18', '荒磯地方', '荒磯', 9, true, ['荒れ磯', '難破船', '鬼の見張り台', '血の池'], '荒磯城', [63, 23], ['p14', 'p15', 'p16', 'p17', 'p19', 'p20']],
  ['p19', '常闇地方', '常闇', 10, false, ['朱雀大路', '荒れ御所', '羅城門', '百鬼の辻'], '常闇砦', [73, 8], ['p18', 'p17', 'p20']],
  ['p20', '幽京地方', '幽京', 10, true, ['夜の市', '五条大橋', '内裏の庭', '鬼の宴'], '幽京城', [58, 11], ['p18', 'p15', 'p19']],
];

function build(): ProvinceDef[] {
  return ROWS.map(([id, name, short, d, boss, f, castleName, pos, adj]) => {
    const base = (d - 1) * 10;
    const battles = (boss ? [2, 4, 6, 8] : [1, 3, 5, 7]).map((i) => base + i) as unknown as [number, number, number, number];
    return {
      id,
      countryId: `c${String(d).padStart(2, '0')}` as CountryId,
      name,
      short,
      difficulty: d,
      terrain: TERRAIN[d - 1],
      battles,
      castle: base + (boss ? 10 : 9),
      fields: [...f, castleName] as [string, string, string, string, string],
      adj,
      gate: GATE_BY_DIFFICULTY[d] ?? 0,
      pos,
      boss,
    };
  });
}

export const PROVINCES: readonly ProvinceDef[] = build();
export const PROVINCE_BY_ID: Readonly<Record<string, ProvinceDef>> = Object.fromEntries(PROVINCES.map((p) => [p.id, p]));

/** 一つの領主が治める国。地方は常に「前線→本城」の順。 */
export interface CountryDef {
  id: CountryId; name: string; short: string; difficulty: number; terrain: string;
  regions: readonly [ProvinceId, ProvinceId]; adj: readonly CountryId[];
  commander: string; guardian: string; gate: number;
}
export const COUNTRIES: readonly CountryDef[] = Array.from({ length: 10 }, (_, i) => {
  const front = PROVINCES[i * 2], capital = PROVINCES[i * 2 + 1];
  return { id: front.countryId, name: `${capital.short}国`, short: capital.short,
    difficulty: i + 1, terrain: front.terrain, regions: [front.id, capital.id] as const,
    adj: [...new Set([...front.adj, ...capital.adj].map(id => PROVINCE_BY_ID[id].countryId))].filter(id => id !== front.countryId),
    // commander＝大将（敵ヒーロー・ADV の敵）、guardian＝本城の番人（data/leaders.ts で入れ替えた章あり）
    commander: CHAPTER_LEADERS[i + 1]?.rival ?? `r${String(i + 1).padStart(2, '0')}`,
    guardian: CHAPTER_LEADERS[i + 1]?.boss ?? `e${String((i + 1) * 10).padStart(3, '0')}`, gate: front.gate };
});
export const COUNTRY_BY_ID: Readonly<Record<string, CountryDef>> = Object.fromEntries(COUNTRIES.map(c => [c.id, c]));
export const countryOfStage = (id: number): CountryDef | null => {
  const p = provinceOfStage(id); return p ? COUNTRY_BY_ID[p.countryId] : null;
};
export const countryStages = (c: CountryDef): number[] => c.regions.flatMap(id => provinceStages(PROVINCE_BY_ID[id]));
export const provinceLabel = (p: ProvinceDef) => `${COUNTRY_BY_ID[p.countryId].name}・${p.name}`;
export const strongholdLabel = (p: ProvinceDef) => p.boss ? '本城' : '砦';
/** 最初の国（難易度壱、チュートリアル）。最初の合戦（ステージ1）を勝つまで、この国の他の戦場は開かない */
export const START_PROVINCE: ProvinceId = 'p01';
export const TUTORIAL_STAGE = 1;

export interface StagePlace {
  province: ProvinceDef;
  /** 国の中の番号 0..4（4 が本城） */
  slot: number;
  castle: boolean;
  /** 戦場の名前 */
  field: string;
}
const PLACE: Record<number, StagePlace> = {};
for (const p of PROVINCES) {
  p.battles.forEach((s, i) => (PLACE[s] = { province: p, slot: i, castle: false, field: p.fields[i] }));
  PLACE[p.castle] = { province: p, slot: 4, castle: true, field: p.fields[4] };
}

/** ステージがどの国の何番目の戦場か */
export const placeOfStage = (stageId: number): StagePlace | null => PLACE[stageId] ?? null;
export const provinceOfStage = (stageId: number): ProvinceDef | null => PLACE[stageId]?.province ?? null;
/** 本城（城攻め）か。掃討は未クリアの本城には使えない */
export const isCastle = (stageId: number) => PLACE[stageId]?.castle === true;
/** 地方の5戦（合戦4つ＋拠点の順） */
export const provinceStages = (p: ProvinceDef) => [...p.battles, p.castle];
const SLOT_NAME = ['一ノ陣', '二ノ陣', '三ノ陣', '四ノ陣', '本城'];
export const slotLabel = (slot: number) => SLOT_NAME[slot] ?? '';
/** 画面の表記（例：「潮見国 一ノ陣 浜の道」「青葉国 本城 青葉城」） */
export function stageLabel(stageId: number, opts: { short?: boolean } = {}): string {
  const pl = PLACE[stageId];
  if (!pl) return `第${stageId}陣`;
  return opts.short ? `${pl.province.short}・${pl.field}` : `${provinceLabel(pl.province)} ${pl.castle ? strongholdLabel(pl.province) : SLOT_NAME[pl.slot]} ${pl.field}`;
}

const cleared = (prof: Profile, stageId: number, difficulty = 0) => (prof.progress[stageKey(stageId, difficulty)]?.clears ?? 0) > 0;

/** 上の難易度が開く戦場（通常の本編の最後）。本編の途中で上の難易度の初回EXP・上乗せが近道にならないように */
export const HARD_UNLOCK_STAGE = 100;
/** 難易度 d を選べるか：一つ下の難易度の第100陣をクリア済み（通常はいつでも。hard.open より上は非表示） */
export const difficultyOpen = (prof: Profile, d: number) => d === 0 || (d <= CONFIG.hard.open && cleared(prof, HARD_UNLOCK_STAGE, d - 1));
/** 上の難易度を選べるか（通常の第100陣をクリア済み） */
export const hardUnlocked = (prof: Profile) => difficultyOpen(prof, 1);

/** 地方拠点を攻略したか（砦または本城を通常難易度でクリア） */
export const isConquered = (prof: Profile, id: string) => {
  const p = PROVINCE_BY_ID[id];
  return !!p && cleared(prof, p.castle);
};
export const conqueredProvinces = (prof: Profile): ProvinceId[] => PROVINCES.filter((p) => cleared(prof, p.castle)).map((p) => p.id);
export const conqueredDistrictCount = (prof: Profile) => conqueredProvinces(prof).length;
export const isCountryConquered = (prof: Profile, id: string) => !!COUNTRY_BY_ID[id] && COUNTRY_BY_ID[id].regions.every(region => isConquered(prof, region));
export const conqueredCountries = (prof: Profile): CountryId[] => COUNTRIES.filter(c => isCountryConquered(prof, c.id)).map(c => c.id);
export const conqueredCount = (prof: Profile) => conqueredCountries(prof).length;
/** 地方の中でクリアした戦場の数（0..5） */
export const provinceClears = (prof: Profile, p: ProvinceDef) => provinceStages(p).filter((s) => cleared(prof, s)).length;

/**
 * 国の状態：
 * - conquered 制圧済み
 * - open      攻め込める（最初の国・制圧した国の隣で関所の条件を満たす・すでに足場がある）
 * - gated     制圧した国の隣だが関所の条件が足りない
 * - fog       まだ隣に制圧した国がない（未踏）
 */
export type ProvinceState = 'conquered' | 'open' | 'gated' | 'fog' | 'frontier';

export function provinceState(prof: Profile, id: string, conquered = new Set(conqueredProvinces(prof))): ProvinceState {
  const p = PROVINCE_BY_ID[id];
  if (!p) return 'fog';
  if (conquered.has(p.id)) return 'conquered';
  if (p.id === START_PROVINCE) return 'open';
  if (provinceStages(p).some((s) => cleared(prof, s))) return 'open';
  const country = COUNTRY_BY_ID[p.countryId];
  // 本城地方は前線の砦を落としてから。国境に接していても横入りさせない。
  if (p.boss) return conquered.has(country.regions[0]) ? 'open' : 'frontier';
  // 旧版で本城地方に足場がある場合も、残った前線へ進める。
  if (country.regions.some(id => provinceStages(PROVINCE_BY_ID[id]).some(s => cleared(prof, s)))) return 'open';
  if (!country.adj.some(id => isCountryConquered(prof, id))) return 'fog';
  return conqueredCount(prof) >= p.gate ? 'open' : 'gated';
}

/** 攻め込める国（まだ制圧していない open の国） */
export function attackableProvinces(prof: Profile): ProvinceId[] {
  const c = new Set(conqueredProvinces(prof));
  return PROVINCES.filter((p) => provinceState(prof, p.id, c) === 'open').map((p) => p.id);
}

/** 国の詳細に出す戦場の状態 */
export type FieldState = 'cleared' | 'open' | 'locked';

/**
 * そのステージに出撃できるか。
 * - 通常難易度：国が制圧済みか攻め込める。合戦は一ノ陣から順番に（前の陣をクリアすると次が開く）、砦・本城は合戦4つをクリアしたあと。
 *   最初の国はチュートリアルとして最初の合戦（ステージ1）を勝つまで他の戦場が開かない。クリア済みの戦場はいつでも出撃できる。
 * - 上の難易度：一つ下の難易度の第100陣をクリアすると開く。第1章（青葉国）から1国ずつ（前の章の本城をその難易度でクリアすると次の章）、
 *   国の中は通常と同じ順番（前線の合戦→砦→本城の地方の合戦→本城）。
 */
export function isStageUnlocked(prof: Profile, stageId: number, difficulty = 0): boolean {
  if (!Number.isInteger(stageId) || stageId < 1 || stageId > 100) return false;
  if (difficulty > 0) {
    if (difficulty > CONFIG.hard.open) return false;
    if (cleared(prof, stageId, difficulty)) return true;
    if (!difficultyOpen(prof, difficulty)) return false;
    const pl = PLACE[stageId];
    if (!pl) return false;
    const ch = Math.ceil(stageId / 10);
    if (ch > 1 && !cleared(prof, (ch - 1) * 10, difficulty)) return false;
    if (pl.province.boss && !cleared(prof, PROVINCE_BY_ID[COUNTRY_BY_ID[pl.province.countryId].regions[0]].castle, difficulty)) return false;
    if (pl.castle) return pl.province.battles.every((s) => cleared(prof, s, difficulty));
    const i = pl.province.battles.indexOf(stageId);
    return i <= 0 || cleared(prof, pl.province.battles[i - 1], difficulty);
  }
  if (cleared(prof, stageId)) return true;
  const pl = PLACE[stageId];
  if (!pl) return false;
  const st = provinceState(prof, pl.province.id);
  if (st !== 'open' && st !== 'conquered') return false;
  if (pl.province.id === START_PROVINCE && stageId !== TUTORIAL_STAGE && !cleared(prof, TUTORIAL_STAGE)) return false;
  if (pl.castle) return pl.province.battles.every((s) => cleared(prof, s));
  const i = pl.province.battles.indexOf(stageId);
  return i <= 0 || cleared(prof, pl.province.battles[i - 1]);
}

export function fieldState(prof: Profile, stageId: number): FieldState {
  if (cleared(prof, stageId)) return 'cleared';
  return isStageUnlocked(prof, stageId) ? 'open' : 'locked';
}

/** 出撃できる未クリアの戦場（通常難易度、ステージID順） */
export function openStages(prof: Profile): number[] {
  const out: number[] = [];
  for (const id of attackableProvinces(prof)) for (const s of provinceStages(PROVINCE_BY_ID[id])) if (!cleared(prof, s) && isStageUnlocked(prof, s)) out.push(s);
  return out.sort((a, b) => a - b);
}

/** 次の目安（TOP の「次：」・オート周回の前進先）：出撃できる未クリアの戦場のうち一番弱い（ステージIDが小さい）もの。全部クリアなら null */
export const nextStage = (prof: Profile): number | null => openStages(prof)[0] ?? null;

/** ランキングの値：制圧した国の数 → 星の合計（通常難易度） */
export function rankValue(prof: Profile): { provinces: number; stars: number } {
  let stars = 0;
  for (const [k, v] of Object.entries(prof.progress)) if (!k.includes(':')) stars += v.stars.filter(Boolean).length;
  return { provinces: conqueredCount(prof), stars };
}

/** 通常難易度でクリアしたステージの数（100で全制圧） */
export const clearedStageCount = (prof: Profile) => Object.entries(prof.progress).filter(([k, v]) => !k.includes(':') && v.clears > 0).length;

/** 画面・API 用の国の一覧の状態 */
export interface ProvinceView {
  id: ProvinceId;
  state: ProvinceState;
  /** 地方の中でクリアした戦場の数（0..5） */
  cleared: number;
  /** 地方の星の合計（0..15、通常難易度） */
  stars: number;
}
export function provinceViews(prof: Profile): ProvinceView[] {
  const c = new Set(conqueredProvinces(prof));
  return PROVINCES.map((p) => {
    let stars = 0;
    for (const s of provinceStages(p)) stars += prof.progress[stageKey(s)]?.stars.filter(Boolean).length ?? 0;
    return { id: p.id, state: provinceState(prof, p.id, c), cleared: provinceClears(prof, p), stars };
  });
}

export function countryViews(prof: Profile) {
  return COUNTRIES.map(c => ({ id: c.id, name: c.name, regions: c.regions, commander: c.commander,
    conquered: isCountryConquered(prof, c.id), cleared: countryStages(c).filter(s => cleared(prof, s)).length,
    districts: c.regions.filter(id => isConquered(prof, id)).length }));
}
